import { useCeleryWorkflowStatusesRetrieve } from "gen/endpoints/celery-workflow-statuses/celery-workflow-statuses";
import { CeleryWorkflowStateMachineFieldsEnum } from "gen/model/celeryWorkflowStateMachineFieldsEnum";
import { CeleryWorkflowStatus } from "gen/model/celeryWorkflowStatus";
import { useEffect } from "react";
import { useMapState } from "shared/hooks/useMapState";

export const useCeleryWorkflowPoll = (
  celeryWorkflowIds: string[] | undefined,
  renderToasts: (is_failed: boolean) => void,
  onSuccess: () => void,
  onError: () => void,
): Map<string, CeleryWorkflowStatus> => {
  const {
    get: getCachedState,
    set: setCachedState,
    has: hasCachedState,
    getFullMap: getCache,
  } = useMapState<string, CeleryWorkflowStatus>();

  const { data: workflowStatusList } = useCeleryWorkflowStatusesRetrieve(
    { id_list: celeryWorkflowIds ?? [] },
    {
      query: {
        // enable only if there is a celery workflow that has not been seen yet or that is not in a terminal state
        enabled:
          celeryWorkflowIds?.some(
            (celeryWorkflowId: string) =>
              !hasCachedState(celeryWorkflowId) ||
              getCachedState(celeryWorkflowId)?.state ===
                CeleryWorkflowStateMachineFieldsEnum.draft ||
              getCachedState(celeryWorkflowId)?.state ===
                CeleryWorkflowStateMachineFieldsEnum.processing,
          ) ?? false,
        refetchInterval: 1000, // Poll every second
      },
    },
  );

  useEffect(() => {
    workflowStatusList?.forEach((workflowStatus) => {
      if (!hasCachedState(workflowStatus.id)) {
        // If we have not seen the workflow before, cache it and don't do anything
        // It means it's either a new workflow or we are re-loading the page. Either way, we don't want to show any toast or refresh the page.
        setCachedState(workflowStatus.id, workflowStatus);
      } else {
        const cached_result = getCachedState(workflowStatus.id);
        if (cached_result != workflowStatus) {
          setCachedState(workflowStatus.id, workflowStatus);
          // Handle new state change

          if (
            workflowStatus.state ==
            CeleryWorkflowStateMachineFieldsEnum.complete
          ) {
            renderToasts(false);
            onSuccess();
          } else if (
            workflowStatus.state == CeleryWorkflowStateMachineFieldsEnum.failed
          ) {
            renderToasts(true);
            onError();
          }
        }
      }
    });
  }, [
    hasCachedState,
    setCachedState,
    getCachedState,
    renderToasts,
    onSuccess,
    onError,
    workflowStatusList,
  ]);
  return getCache();
};
